import JSONbig from 'json-bigint'
import { API_BASE } from './config.js'

const jsonParser = JSONbig({ storeAsString: true })

function getToken() {
  return localStorage.getItem('token') || ''
}

function request(method, path, body) {
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + getToken()
  }
  const options = {
    method,
    headers,
    credentials: 'include'
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return fetch(`${API_BASE}${path}`, options)
    .then(res => res.text())
    .then(text => jsonParser.parse(text))
}

/**
 * 查询好友分享给我的全部工作表（含随工作区继承的）
 * @param {string|number} userId 好友用户 ID
 * @returns {Promise<{code:number,message:string,data:Array,success:boolean,timestamp:string}>}
 */
export function listFriendProjects(userId) {
  return request('GET', `/api/v1/friends/${userId}/projects`)
}

/**
 * 查询好友工作表在某日期区间内的时间块（用于表格视图）
 * @param {Object} params
 * @param {string|number} params.userId 好友用户 ID
 * @param {string|number} params.workspaceId 工作区 ID
 * @param {string|number} params.projectId 工作表 ID
 * @param {string} params.startDate 开始日期 yyyy-MM-dd
 * @param {string} params.endDate 结束日期 yyyy-MM-dd
 * @param {boolean} [params.refresh] 是否强制刷新（true 时后端清缓存回源），默认 false
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function getFriendTimeBlocksRange(params) {
  const query = new URLSearchParams({
    workspaceId: String(params.workspaceId),
    projectId: String(params.projectId),
    startDate: params.startDate,
    endDate: params.endDate
  })
  if (params.refresh) {
    query.append('refresh', 'true')
  }
  return request('GET', `/api/v1/friends/${params.userId}/timeline/range?${query.toString()}`)
}

/**
 * 对工作表授权给某好友（只读 / 编辑 + 分类统计表查看权）。
 * @param {string|number} projectId 工作表 ID
 * @param {string|number} friendId 好友用户 ID
 * @param {string} permission READ / EDIT
 * @param {boolean} [categoryVisible] 是否开放「分类（周累计）」表格的查看权限，默认 false
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function grantProject(projectId, friendId, permission, categoryVisible) {
  return request('POST', `/api/v1/shares/projects/${projectId}/grants`, {
    friendId,
    permission,
    categoryVisible: !!categoryVisible
  })
}

/**
 * 查询工作表的全部授权记录。
 * @param {string|number} projectId 工作表 ID
 * @returns {Promise<{code:number,message:string,data:Array,success:boolean,timestamp:string}>}
 */
export function listGrants(projectId) {
  return request('GET', `/api/v1/shares/projects/${projectId}/grants`)
}

/**
 * 撤销对某好友的授权。
 * @param {string|number} projectId 工作表 ID
 * @param {string|number} friendId 好友用户 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function revokeGrant(projectId, friendId) {
  return request('DELETE', `/api/v1/shares/projects/${projectId}/grants/${friendId}`)
}

/**
 * 查询工作表的跨端同步版本号（轻量探针）。
 *
 * 只回一个整数版本号，客户端以较长间隔轮询；任何时间块写操作都会使其自增，
 * 发现版本变化后再拉全量时间块，用于「A 端改工作表 → B 端自动同步」。
 * 自己的多端与好友页浏览他人工作表共用同一接口（后端按可见性鉴权）。
 *
 * @param {string|number} projectId 工作表 ID
 * @returns {Promise<{code:number,message:string,data:{version:number},success:boolean,timestamp:string}>}
 */
export function getProjectVersion(projectId) {
  return request('GET', `/api/v1/projects/${projectId}/version`)
}

/**
 * 查询「我」对某个好友工作表（ownerId 的工作表）的访问权限。
 * @param {string|number} ownerId 工作表所有者（好友）用户 ID
 * @param {string|number} projectId 工作表 ID
 * @returns {Promise<{code:number,message:string,data:{permission:string|null,categoryVisible:boolean},success:boolean,timestamp:string}>}
 *          data.permission 为 'READ' / 'EDIT' 或 null（无权限）；
 *          data.categoryVisible 表示能否查看该表的「分类（周累计）」统计表
 */
export function getFriendProjectAccess(ownerId, projectId) {
  return request('GET', `/api/v1/friends/${ownerId}/projects/${projectId}/permission`)
}

/**
 * 查询好友某张工作表下的分类（标签）列表。
 *
 * 好友页的时间块需要「分类图标 + 名称 + 颜色」才能与自己的时间表渲染一致，
 * 而时间块接口只带 tagId，所以这里单独取一次该表的分类。
 *
 * @param {string|number} ownerId 工作表所有者（好友）用户 ID
 * @param {string|number} projectId 工作表 ID
 * @returns {Promise<{code:number,message:string,data:Array,success:boolean,timestamp:string}>}
 */
export function listFriendTags(ownerId, projectId) {
  return request('GET', `/api/v1/friends/${ownerId}/projects/${projectId}/tags`)
}

/**
 * 以「被授权编辑的好友」身份，在好友工作表下新建分类。
 *
 * 「我的分类」跟随时间表的 EDIT 授权一起开放：能改时间表就能维护分类，
 * 否则好友代填时间块时挑不到自己需要的分类。
 *
 * @param {string|number} ownerId 工作表所有者（好友）用户 ID
 * @param {Object} params 与 CreateTagRequest 对齐：{ projectId, name, color, icon }
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function createFriendTag(ownerId, params) {
  return request('POST', `/api/v1/friends/${ownerId}/tags`, params)
}

/**
 * 以「被授权编辑的好友」身份，修改好友工作表的分类。
 * @param {string|number} ownerId 工作表所有者（好友）用户 ID
 * @param {string|number} tagId 分类 ID
 * @param {Object} params 与 UpdateTagRequest 对齐：{ name, color, icon }
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateFriendTag(ownerId, tagId, params) {
  return request('PUT', `/api/v1/friends/${ownerId}/tags/${tagId}`, params)
}

/**
 * 以「被授权编辑的好友」身份，删除好友工作表的分类。
 * @param {string|number} ownerId 工作表所有者（好友）用户 ID
 * @param {string|number} tagId 分类 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function deleteFriendTag(ownerId, tagId) {
  return request('DELETE', `/api/v1/friends/${ownerId}/tags/${tagId}`)
}

/**
 * 查询我的互动通知（评论 / 点赞）。
 * @param {number} [limit=50] 最大条数
 * @returns {Promise<{code:number,message:string,data:{items:Array,unreadCounts:Object},success:boolean,timestamp:string}>}
 */
export function listNotifications(limit = 50) {
  return request('GET', `/api/v1/notifications?limit=${limit}`)
}

/**
 * 标记通知已读。
 * @param {Object} [payload]
 * @param {string[]} [payload.types] 按类型标记（COMMENT / LIKE）
 * @param {string[]} [payload.ids] 按 ID 标记
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function markNotificationsRead(payload) {
  return request('POST', '/api/v1/notifications/read', payload || {})
}

/**
 * 以「被授权编辑的好友」身份，在好友时间表里新增时间块。
 * @param {string|number} ownerId 时间表所有者（好友）用户 ID
 * @param {Object} params 与 CreateTimeBlockRequest 对齐
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function createFriendTimeBlock(ownerId, params) {
  return request('POST', `/api/v1/friends/${ownerId}/timeblocks`, params)
}

/**
 * 以「被授权编辑的好友」身份，更新时间块执行状态。
 *
 * @param {string|number} ownerId 时间表所有者（好友）用户 ID
 * @param {string|number} blockId 时间块 ID
 * @param {string} status 目标状态：PLANNED / RUNNING / COMPLETED
 * @param {string} [actualEndTime] 实际结束时间（ISO 8601）。仅 RUNNING → COMPLETED 生效，
 *        跨天结束时传「当天 23:59:59」把今天这段收尾；不传取当前时刻。
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateFriendTimeBlockStatus(ownerId, blockId, status, actualEndTime) {
  const body = { executionStatus: status }
  if (actualEndTime) body.actualEndTime = actualEndTime
  return request('PUT', `/api/v1/friends/${ownerId}/timeblocks/${blockId}/status`, body)
}

/**
 * 以「被授权编辑的好友」身份，修改时间块。
 * @param {string|number} ownerId 时间表所有者（好友）用户 ID
 * @param {string|number} blockId 时间块 ID
 * @param {Object} params 与 UpdateTimeBlockRequest 对齐
 * @returns {Promise<{code:number,message:string,data:Object,success:boolean,timestamp:string}>}
 */
export function updateFriendTimeBlock(ownerId, blockId, params) {
  return request('PUT', `/api/v1/friends/${ownerId}/timeblocks/${blockId}`, params)
}

/**
 * 以「被授权编辑的好友」身份，删除时间块。
 * @param {string|number} ownerId 时间表所有者（好友）用户 ID
 * @param {string|number} blockId 时间块 ID
 * @returns {Promise<{code:number,message:string,data:null,success:boolean,timestamp:string}>}
 */
export function deleteFriendTimeBlock(ownerId, blockId) {
  return request('DELETE', `/api/v1/friends/${ownerId}/timeblocks/${blockId}`)
}
